import { storeUtmParams } from "~/helpers/utmParams"
import { ANALYTICS_EVENTS } from "~/helpers/analyticsEvents"

import type { FC, ReactNode } from "react"
import {
  useEffect,
  useCallback,
  createContext,
  useContext,
  useMemo,
  useRef
} from "react"

import { useConfigContext } from "~/providers/ConfigProvider"
import { useSearchParams } from "@remix-run/react"
import TagManager from "react-gtm-module"
import {
  collectionToGTM,
  productToGTM,
  shopifyLineItemToGTM
} from "~/helpers/gtm"
import type { WithProductFragment } from "~/graphql"
import type { WithShopifyCartLineFragment } from "~/shopify"

// Initialize data layer so that we can load measurement values.
if (typeof window !== "undefined") {
  window.dataLayer = window.dataLayer || []
}

let gtmInitialized = false

export function injectClarityScript(projectId: string) {
  try {
    ;(function (c: any, l: Document, a: string, r: string, i: string) {
      if (l.getElementById("clarity-script")) {
        return
      }
      c[a] =
        c[a] ||
        function (...args: any[]) {
          ;(c[a].q = c[a].q || []).push(args)
        }
      const t = l.createElement(r) as HTMLScriptElement
      t.async = true
      t.src = "https://www.clarity.ms/tag/" + i
      t.id = "clarity-script"
      const y = l.getElementsByTagName(r)[0]
      y.parentNode?.insertBefore(t, y)
    })(window, document, "clarity", "script", projectId)
    return
  } catch (_error) {
    return
  }
}

type EventProps = {
  [key: string]: any
}

interface User {
  clientId: () => Promise<unknown>
}

interface AnalyticsContextValue {
  user: () => User
  page: (path?: string, url?: string, category?: string) => void
  track: (eventName: string, data?: any, frontendBase?: string) => void
  identify: (properties?: EventProps) => void
}

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(
  undefined
)

export const AnalyticsProvider: FC<{
  children?: ReactNode
}> = ({ children }) => {
  const config = useConfigContext()
  const previousPageLocationRef = useRef<string | undefined>(undefined)
  const tagManagerArgs = useMemo(
    () => ({
      gtmId: config.gtmId
    }),
    [config.gtmId]
  )

  useEffect(() => {
    if (typeof window !== "undefined" && !config.disableGTM) {
      function loadGTM() {
        if (gtmInitialized) {
          return
        }
        gtmInitialized = true
        TagManager.initialize(tagManagerArgs)
      }

      function trigger() {
        loadGTM()
      }
      const events = ["scroll", "pointerdown", "keydown"]
      events.forEach(evt =>
        window.addEventListener(evt, trigger, { once: true, passive: true })
      )

      let idleCallbackId: number
      let timeoutId: string | number | NodeJS.Timeout | undefined

      if ("requestIdleCallback" in window) {
        idleCallbackId = requestIdleCallback(loadGTM, {
          timeout: config.gtmLoadTimeout
        })
      } else {
        timeoutId = setTimeout(loadGTM, config.gtmLoadTimeout)
      }

      // Cleanup function to remove event listeners on unmount
      return () => {
        events.forEach(evt => {
          window.removeEventListener(evt, trigger)
        })
        if (idleCallbackId) {
          cancelIdleCallback(idleCallbackId)
        }
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
      }
    }
  }, [tagManagerArgs, config.disableGTM, config.gtmLoadTimeout])

  useEffect(() => {
    if (typeof window === "undefined" || !config.microsoftClarityProjectId) {
      return
    }

    injectClarityScript(config.microsoftClarityProjectId)
  }, [config.microsoftClarityProjectId])

  const page = useCallback(
    (path?: string, url?: string, category?: string) => {
      if (config.disableGTM || typeof window === "undefined") return

      const pagePath =
        path || `${window.location.pathname}${window.location.search}`
      const pageUrl = url || window.location.href
      const pageReferrer =
        previousPageLocationRef.current || document.referrer || undefined

      window.dataLayer.push({
        event: ANALYTICS_EVENTS.PAGE_VIEW,
        // GA4 standard page_view params.
        page_path: pagePath,
        page_location: pageUrl,
        page_referrer: pageReferrer,
        page_title: document.title,
        ...(category ? { page_category: category } : {})
      })

      previousPageLocationRef.current = pageUrl
    },
    [config.disableGTM]
  )

  useEffect(() => storeUtmParams(), [])

  useTikTokAnalytics()
  useSproutSocialAnalytics()
  useCampAnonymousId()

  const trackGTM = useCallback(
    (eventName: string, properties?: EventProps | undefined) => {
      if (config.disableGTM) return
      window.dataLayer.push({
        event: eventName,
        ...properties
      })
    },
    [config.disableGTM]
  )

  const resetGTMecommerce = useCallback(() => {
    if (config.disableGTM) return
    window.dataLayer.push({
      ecommerce: undefined
    })
  }, [config.disableGTM])

  const track = useCallback(
    (eventName: string, data?: any, frontendBase?: string) => {
      frontendBase = frontendBase || config.frontendBase
      if (!frontendBase) return
      const handleProductEvents = () => {
        if (!data.product) return
        const gtmProduct = productToGTM(
          frontendBase,
          data.product,
          data.quantity
        )
        resetGTMecommerce()
        trackGTM(eventName, {
          ecommerce: {
            items: [gtmProduct]
          },
          ...data,
          product: undefined
        })
      }

      const handleCartViewed = () => {
        if (!data) return
        const items = data.map((li: WithShopifyCartLineFragment) => {
          if (li.__typename !== "CartLine") return {}
          return shopifyLineItemToGTM(frontendBase, li)
        })
        resetGTMecommerce()
        trackGTM(eventName, { ecommerce: { items } })
      }

      switch (eventName) {
        case ANALYTICS_EVENTS.PRODUCT_LIST_VIEWED:
          if (!data?.collection || !data?.newProducts) return
          resetGTMecommerce()
          trackGTM(eventName, {
            ecommerce: {
              ...collectionToGTM(frontendBase, data.collection),
              items: data?.newProducts.map((p: WithProductFragment) =>
                productToGTM(frontendBase, p)
              )
            }
          })
          break
        case ANALYTICS_EVENTS.PRODUCT_CLICKED:
        case ANALYTICS_EVENTS.PRODUCT_ADDED:
        case ANALYTICS_EVENTS.PRODUCT_VIEWED:
        case ANALYTICS_EVENTS.PRODUCT_NOTIFICATION_REQUESTED:
        case ANALYTICS_EVENTS.PRODUCT_NOTIFICATION_REQUESTED_FROM_CARD:
        case ANALYTICS_EVENTS.SEE_OPTIONS:
        case ANALYTICS_EVENTS.ADD_TO_CART:
          handleProductEvents()
          break
        case ANALYTICS_EVENTS.PRODUCT_REMOVED:
          resetGTMecommerce()
          trackGTM(eventName, {
            ecommerce: {
              items: [shopifyLineItemToGTM(frontendBase, data)]
            }
          })
          break
        case ANALYTICS_EVENTS.CART_VIEWED:
          handleCartViewed()
          break
        default:
          trackGTM(eventName, data)
      }
    },
    [trackGTM, config.frontendBase, resetGTMecommerce]
  )

  const identifyGTM = useCallback(
    (userData: EventProps) => {
      if (typeof window !== "undefined" && userData.email) {
        window.dataLayer.push({
          user_data: {
            email: userData.email
          }
        })
        trackGTM(ANALYTICS_EVENTS.IDENTIFY, userData)
      }
    },
    [trackGTM]
  )

  const identify = useCallback(
    (properties?: EventProps) => {
      if (!config.disableGTM && properties) {
        identifyGTM(properties)
      }
    },
    [config.disableGTM, identifyGTM]
  )

  const user = useCallback(() => {
    const user: User = {
      clientId: async () =>
        await getGoogleAnalyticsValue(
          config.googleAnalyticsMeasurementId,
          "client_id"
        )
    }
    return user
  }, [config.googleAnalyticsMeasurementId])

  useEffect(() => {
    const handleKlaviyoForms = (e: any) => {
      if (e.detail?.type == "open") {
        trackGTM(ANALYTICS_EVENTS.KLAVIYO_FORM_OPENED, {
          form_id: e.detail.formId
        })
      }

      if (e.detail?.type == "embedOpen") {
        trackGTM(ANALYTICS_EVENTS.KLAVIYO_FORM_EMBED_OPENED, {
          form_id: e.detail.formId
        })
      }

      if (e.detail?.type == "close") {
        trackGTM(ANALYTICS_EVENTS.KLAVIYO_FORM_CLOSED, {
          form_id: e.detail.formId
        })
      }

      if (e.detail?.type == "submit") {
        trackGTM(ANALYTICS_EVENTS.KLAVIYO_FORM_SUBMITTED, {
          form_id: e.detail.formId
        })
      }
    }

    window.addEventListener("klaviyoForms", handleKlaviyoForms)

    return () => {
      window.removeEventListener("klaviyoForms", handleKlaviyoForms)
    }
  }, [trackGTM])

  const value = useMemo(
    () => ({
      user,
      page,
      track,
      identify
    }),
    [page, track, identify, user]
  )

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  )
}

/**
 * Hook.
 */

export const useAnalytics = () => {
  const value = useContext(AnalyticsContext)
  if (!value) throw new Error("Analytics provider is missing.")
  return value
}

/**
 * Helpers for fetching GA4 session data.
 */

const getGoogleAnalyticsValueCache: Record<string, unknown> = {}

export const getGoogleAnalyticsValue = (
  measurementId: string,
  value: string
) => {
  // This is really needed to to interact with the data layer.
  function pushToDataLayer(...args: any[]) {
    window.dataLayer.push(arguments)
  }

  return new Promise((resolve, error) => {
    if (getGoogleAnalyticsValueCache[value]) {
      resolve(getGoogleAnalyticsValueCache[value])
    } else {
      const timeout = setTimeout(() => {
        error(`Timeout fetching GA value: ${value}`)
      }, 2000)

      pushToDataLayer("get", measurementId, value, (response: string) => {
        clearTimeout(timeout)

        getGoogleAnalyticsValueCache[value] = response
        resolve(response)
      })
    }
  })
}

export const getGoogleAnalyticsSessionData = async (measurementId: string) => {
  return Promise.all([
    getGoogleAnalyticsValue(measurementId, "client_id"),
    getGoogleAnalyticsValue(measurementId, "session_id"),
    getGoogleAnalyticsValue(measurementId, "session_number")
  ])
    .then(values => ({
      clientId: String(values[0]),
      sessionId: String(values[1]),
      sessionNumber: parseInt(String(values[2]), 10)
    }))
    .catch(e => {
      console.error(e)
      return null
    })
}

interface MockProviderProps {
  action: (name: string) => (...args: any[]) => any
  children?: ReactNode
}

export const MockedAnalyticsProvider: FC<MockProviderProps> = ({
  children,
  action
}) => {
  const value = useMemo(
    () => ({
      user: () =>
        ({
          clientId: action("clientId")
        }) as User,
      page: action("page"),
      track: action("track"),
      identify: action(ANALYTICS_EVENTS.IDENTIFY)
    }),
    [action]
  )

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  )
}

// Simple no-op provider for Storybook that doesn't make any async calls
export const StorybookAnalyticsProvider: FC<{ children?: ReactNode }> = ({
  children
}) => {
  const value = useMemo(
    () => ({
      user: () => ({ clientId: () => Promise.resolve("storybook-client-id") }),
      page: () => {},
      track: () => {},
      identify: () => {}
    }),
    []
  )

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  )
}

export const useTikTokAnalytics = () => {
  const [params] = useSearchParams()
  const ttclid = params.get("ttclid")
  const ttl = 86400000 // Time to live for ttclid, 24 hours in milliseconds

  useEffect(() => {
    if (ttclid) {
      if (typeof window !== "undefined") {
        const now = Date.now()
        localStorage.setItem("ttclid", ttclid)
        localStorage.setItem("ttclid_timestamp", now.toString())

        window.dataLayer.push({
          event: ANALYTICS_EVENTS.IDENTIFY,
          ttclid
        })
      }
    }
  }, [ttclid])

  let storedTtclid
  if (typeof window !== "undefined") {
    storedTtclid = localStorage.getItem("ttclid")
    const storedTimestamp = localStorage.getItem("ttclid_timestamp")
    const parsedTimestamp = Number(storedTimestamp)
    const hasTimestamp = !!storedTimestamp
    const hasValidTimestamp = Number.isFinite(parsedTimestamp)
    const isExpired =
      hasTimestamp && hasValidTimestamp
        ? Date.now() - parsedTimestamp >= ttl
        : false
    const shouldClear =
      !!storedTtclid && hasTimestamp && (!hasValidTimestamp || isExpired)

    if (shouldClear) {
      localStorage.removeItem("ttclid")
      localStorage.removeItem("ttclid_timestamp")
      storedTtclid = null
    }
  }

  return { ttclid: storedTtclid }
}

export const useSproutSocialAnalytics = () => {
  const [params] = useSearchParams()
  const sproutTxidParam = params.get("transaction_id")
  const ttl = 86400000 // Time to live for sprout_txid, 24 hours in milliseconds

  useEffect(() => {
    if (sproutTxidParam) {
      if (typeof window !== "undefined") {
        const now = Date.now()
        localStorage.setItem("sprout_txid", sproutTxidParam)
        localStorage.setItem("sprout_txid_timestamp", now.toString())
      }
    }
  }, [sproutTxidParam])

  let storedSproutTxid
  if (typeof window !== "undefined") {
    storedSproutTxid = localStorage.getItem("sprout_txid")
    const storedTimestamp = localStorage.getItem("sprout_txid_timestamp")
    const parsedTimestamp = Number(storedTimestamp)
    const hasTimestamp = !!storedTimestamp
    const hasValidTimestamp = Number.isFinite(parsedTimestamp)
    const isExpired =
      hasTimestamp && hasValidTimestamp
        ? Date.now() - parsedTimestamp >= ttl
        : false
    const shouldClear =
      !!storedSproutTxid && hasTimestamp && (!hasValidTimestamp || isExpired)

    if (shouldClear) {
      localStorage.removeItem("sprout_txid")
      localStorage.removeItem("sprout_txid_timestamp")
      storedSproutTxid = null
    }
  }

  return { sproutTxid: storedSproutTxid }
}

export const useCampAnonymousId = () => {
  let id = undefined

  if (typeof window !== "undefined") {
    id = localStorage.getItem("ca_id")

    if (!id) {
      id = window.crypto.randomUUID()
    }
  }

  useEffect(() => {
    if (typeof window !== "undefined" && id) {
      localStorage.setItem("ca_id", id)
    }
  }, [id])

  return id
}

export const getCampAnonymousId = () => {
  if (typeof window !== "undefined") {
    return localStorage.getItem("ca_id")
  } else {
    return undefined
  }
}
